export function markdownBoldToHtml(text: string): string {
  return String(text || '').replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>');
}

export function visiblePlainRecipeText(text: string): string {
  return String(text || '')
    .replace(/\*\*([^*]+)\*\*/g, '$1')
    .replace(/<(?:strong|b)>([\s\S]*?)<\/(?:strong|b)>/gi, '$1')
    .replace(/<[^>]+>/g, '');
}

export function isIngredientGroupLabel(text: string): boolean {
  const visible = visiblePlainRecipeText(text).trim();
  if (!visible) return false;
  if (/\d/.test(visible)) return false;
  if (/[a-zåäö]/.test(visible)) return false;
  return /[A-ZÅÄÖ]{2,}/.test(visible.replace(/[^A-ZÅÄÖ]/g, ''));
}

export function ensureIngredientGroupBold(text: string): string {
  const normalized = markdownBoldToHtml(String(text || '')).trim();
  if (!normalized || isFieldBold(normalized) || !isIngredientGroupLabel(normalized)) {
    return normalized;
  }
  return `<strong>${visiblePlainRecipeText(normalized)}</strong>`;
}

export function normalizeRecipeFieldValues<T extends {
  receptnamn: string;
  beskrivning: string;
  tillagningstid: string;
  antalPortioner: string;
  ingredienser: string[];
  instruktioner: string[];
}>(values: T): T {
  return {
    ...values,
    receptnamn: markdownBoldToHtml(values.receptnamn),
    beskrivning: markdownBoldToHtml(values.beskrivning),
    tillagningstid: markdownBoldToHtml(values.tillagningstid),
    antalPortioner: markdownBoldToHtml(values.antalPortioner),
    ingredienser: values.ingredienser.map((item) => markdownBoldToHtml(item)),
    instruktioner: values.instruktioner.map((item) => markdownBoldToHtml(item)),
  };
}

export function normalizeProposedRecipeFieldValues<T extends {
  receptnamn: string;
  beskrivning: string;
  tillagningstid: string;
  antalPortioner: string;
  ingredienser: string[];
  instruktioner: string[];
}>(values: T): T {
  const normalized = normalizeRecipeFieldValues(values);
  return {
    ...normalized,
    ingredienser: normalized.ingredienser.map(ensureIngredientGroupBold),
  };
}

export function isFieldBold(text: string): boolean {
  const html = markdownBoldToHtml(String(text || '')).trim();
  return /^<(?:strong|b)>[\s\S]*<\/(?:strong|b)>$/i.test(html);
}

export function toggleFieldBold(text: string): string {
  const html = markdownBoldToHtml(String(text || '')).trim();
  const wrapped = html.match(/^<(?:strong|b)>([\s\S]*)<\/(?:strong|b)>$/i);
  if (wrapped) return wrapped[1];
  if (!html) return String(text || '');
  return `<strong>${html}</strong>`;
}

export function toggleSelectionBold(text: string, start: number, end: number): string {
  const value = String(text || '');
  const from = Math.max(0, Math.min(start, end, value.length));
  const to = Math.max(0, Math.max(start, end), from);
  if (from === to) return toggleFieldBold(value);
  return value.slice(0, from) + toggleFieldBold(value.slice(from, to)) + value.slice(to);
}

export type FormattedPart = {
  text: string;
  bold: boolean;
};

export function visibleRecipeText(text: string): string {
  return splitFormattedRecipeText(text).map((part) => part.text).join('');
}

export function tokenHasBold(source: string, token: string): boolean {
  if (!String(token || '').trim()) return false;
  if (isFieldBold(source)) return true;
  return splitFormattedRecipeText(source).some((part) => part.bold && part.text.includes(token));
}

export function recipeTextToEditorHtml(text: string): string {
  const html = markdownBoldToHtml(String(text || '')).replace(/\n/g, '<br>');
  return html.trim() ? `<p>${html}</p>` : '<p></p>';
}

export function editorHtmlToRecipeText(html: string): string {
  const normalized = String(html || '')
    .replace(/<b(\s[^>]*)?>/gi, '<strong>')
    .replace(/<\/b>/gi, '</strong>')
    .replace(/<br\s*\/?>/gi, '\n')
    .replace(/<\/p>/gi, '\n')
    .replace(/<\/div>/gi, '\n')
    .replace(/<p[^>]*>/gi, '')
    .replace(/<div[^>]*>/gi, '')
    .replace(/<(?!\/?strong\b)[^>]+>/gi, '');
  return markdownBoldToHtml(normalized).replace(/\n{3,}/g, '\n\n').replace(/^\n+|\n+$/g, '');
}

export function splitFormattedRecipeText(text: string): FormattedPart[] {
  const source = String(text || '');
  if (!source) return [];
  const parts: FormattedPart[] = [];
  const pattern = /\*\*([^*]+)\*\*|<(?:strong|b)>([\s\S]*?)<\/(?:strong|b)>/gi;
  let lastIndex = 0;
  let match: RegExpExecArray | null = pattern.exec(source);
  while (match) {
    if (match.index > lastIndex) {
      parts.push({ text: source.slice(lastIndex, match.index), bold: false });
    }
    parts.push({ text: match[1] || match[2] || '', bold: true });
    lastIndex = match.index + match[0].length;
    match = pattern.exec(source);
  }
  if (lastIndex < source.length) {
    parts.push({ text: source.slice(lastIndex), bold: false });
  }
  return parts;
}
